
<template>
  <div class="simpark-contact">

    <!-- Success message -->
    <transition name="fade" mode="out-in">
      <div v-if="sent" class="thanksBox">
        <p class="thanksText">
          Thanks for contacting SimPark. We'll get back to you shortly.
        </p>
      </div>
    </transition>

    <!-- Contact form -->
    <b-form v-if="!sent" @submit.prevent="submitForm">

      <b-row class="rowPadMob">

        <b-col md="6" sm="12">
          <b-form-input
            v-model.trim="name"
            type="text"
            placeholder="Your Name"
            autocomplete="name"
            required
          />
        </b-col>

        <b-col md="6" sm="12">
          <b-form-input
            v-model.trim="email"
            type="email"
            placeholder="Your Email Address"
            autocomplete="email"
            required
          />
        </b-col>

        <b-col cols="12">
          <b-form-input
            v-model.trim="subject"
            type="text"
            placeholder="Subject"
            maxlength="150"
            required
          />
        </b-col>

        <b-col cols="12">
          <b-form-textarea
            v-model.trim="message"
            rows="6"
            class="contact-message"
            placeholder="How can we help?"
            required
          />
        </b-col>

        <!-- Error message -->
        <b-col v-if="text" cols="12">
          <p class="errorText" role="alert">
            {{ text }}
          </p>
        </b-col>

        <!-- Submit -->
        <b-col cols="12" class="text-center">
          <button
            type="submit"
            class="HomepageBtn"
            :disabled="!formValid || sending"
            :class="{ disabled: !formValid || sending }"
          >
            {{ sending ? 'Sending...' : 'Send Enquiry' }}
          </button>
        </b-col>

        <!-- Cloudflare Turnstile -->
        <b-col cols="12" class="text-center">
          <div ref="turnstile" class="turnstileContainer"></div>
        </b-col>

      </b-row>
    </b-form>

  </div>
</template>

<script>
export default {
  data() {
    return {
      name: "",
      email: "",
      subject: "",
      message: "",

      text: "",
      sent: false,
      sending: false,

      turnstileToken: null,
      turnstileWidgetId: null,
      turnstileInterval: null
    };
  },

  computed: {
    formValid() {
      return (
        this.name.trim() !== "" &&
        this.email.trim() !== "" &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email) &&
        this.subject.trim() !== "" &&
        this.message.trim() !== ""
      );
    }
  },

  methods: {
    loadTurnstile() {
      this.turnstileInterval = setInterval(() => {
        if (
          window.turnstile &&
          this.$refs.turnstile &&
          this.turnstileWidgetId === null
        ) {
          clearInterval(this.turnstileInterval);
          this.turnstileInterval = null;

          this.turnstileWidgetId = window.turnstile.render(
            this.$refs.turnstile,
            {
              sitekey: "0x4AAAAAAEtrRr92OBffj1xG",

              callback: (token) => {
                this.turnstileToken = token;
              },

              "expired-callback": () => {
                this.turnstileToken = null;
              },

              "error-callback": () => {
                this.turnstileToken = null;
              }
            }
          );
        }
      }, 100);
    },

    async submitForm() {
      this.text = "";

      if (!this.formValid) {
        this.text = "Please complete all fields with valid information.";
        return;
      }

      if (!this.turnstileToken) {
        this.text = "Please complete the security check.";
        return;
      }

      this.sending = true;

      try {
        const response = await fetch("/api/contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            name: this.name,
            email: this.email,
            subject: this.subject,
            message: this.message,
            turnstileToken: this.turnstileToken
          })
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(
            result.message || "Unable to send your enquiry."
          );
        }

        this.sent = true;
        this.name = "";
        this.email = "";
        this.subject = "";
        this.message = "";
        this.turnstileToken = null;

      } catch (error) {
        console.error("SimPark contact form error:", error);

        this.text =
          error.message === "Failed to fetch"
            ? "Unable to send your enquiry. Please try again."
            : error.message || "Something went wrong. Please try again.";

        this.resetTurnstile();

      } finally {
        this.sending = false;
      }
    },

    resetTurnstile() {
      if (
        window.turnstile &&
        this.turnstileWidgetId !== null
      ) {
        window.turnstile.reset(this.turnstileWidgetId);
      }

      this.turnstileToken = null;
    }
  },

  mounted() {
    this.loadTurnstile();
  },

  beforeDestroy() {
    if (this.turnstileInterval) {
      clearInterval(this.turnstileInterval);
    }

    if (
      window.turnstile &&
      this.turnstileWidgetId !== null
    ) {
      window.turnstile.remove(this.turnstileWidgetId);
    }
  }
};
</script>

<style scoped>
.simpark-contact {
  width: 100%;
  text-align: left;
  padding-bottom: 5%;
}

.simpark-contact .row {
  margin-left: 0;
  margin-right: 0;
}

.simpark-contact .form-control,
.simpark-contact .custom-select {
  min-height: 52px;
  border-radius: 0;
  font-size: 16px;
  margin-bottom: 15px;
  padding: 12px;
  color: #4e4e4e;
}

.simpark-contact .form-control::placeholder {
  color: #4e4e4e;
  opacity: 1;
}

.simpark-contact .contact-message {
  min-height: 150px;
  resize: vertical;
}

.simpark-contact .HomepageBtn {
  background-color: #d80e1f;
  border: 1px solid #d80e1f;
  border-radius: 50px;
  width: 252px;
  min-height: 54px;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  font-family: "noto-sans", sans-serif;
  cursor: pointer;
  margin: 20px auto;
  display: block;
}

.simpark-contact .HomepageBtn:hover:not(:disabled) {
  background-color: #707070;
  border-color: #707070;
}

.simpark-contact .HomepageBtn.disabled,
.simpark-contact .HomepageBtn:disabled {
  background-color: #ccc;
  border-color: transparent;
  cursor: not-allowed;
}

.simpark-contact .thanksBox {
  text-align: center;
  border: 2px solid #fff;
  padding: 20px;
}

.simpark-contact .thanksText {
  font-size: 20px;
  color: #fff;
  margin: 0;
}

.simpark-contact .errorText {
  color: #d80e1f;
  margin-bottom: 10px;
}

.simpark-contact .turnstileContainer {
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .simpark-contact .rowPadMob {
    padding-left: 15px;
    padding-right: 15px;
  }

  .simpark-contact .HomepageBtn {
    max-width: 100%;
  }
}
</style>
